import { IPane, SHEET_SIZE } from '../sheet-layout';
import { EDxfEntityKind, IDxfEntity, IDxfLine, IDxfPoint, translateEntity } from './dxf-entities';
import { IDxfPaneFit, PAPER_MM_PER_PX } from './dxf-fit';
import { EDxfSheetLayer } from './dxf-layers';
import { IPlacedView, titleEntity } from './dxf-model-layout';
import { paneEntities } from './dxf-primitives';
import { ITitleBlockData, TITLE_BLOCK_SIZE, titleBlockEntities } from './dxf-title-block';

/*
 * The printable sheet in paper space. The screen sheet of 1600 × 1200 px is a paper sheet of
 * 423 × 318 mm with every view at its standard scale; it does not fit A3 with the ГОСТ 2.301 frame
 * and the 185 × 55 mm inscription, so the file sets it on A2 landscape: the frame 20 mm from the
 * left edge and 5 mm from the others, the inscription in the bottom-right corner, the views
 * centred in the room above it. Each view with a scale is a viewport onto its place in model
 * space at «1 : N»; the spiral's rotation dial has no millimetre scale and is drawn on the paper.
 */

/** A2 landscape, millimetres. */
export const DXF_PAPER_SIZE: { width: number; height: number } = { width: 594, height: 420 };
/** ГОСТ 2.301: the frame is 20 mm from the binding edge and 5 mm from the other three. */
const FRAME_MARGIN: { left: number; right: number; top: number; bottom: number } = { left: 20, right: 5, top: 5, bottom: 5 };

/** One viewport of the sheet: its rectangle on paper and the model window it shows. */
export interface IDxfViewport {
    /** Centre on paper, millimetres. */
    center: IDxfPoint;
    width: number;
    height: number;
    /** Centre of the model window, model millimetres. */
    viewCenter: IDxfPoint;
    /** Height of the model window, model millimetres: `height × N`. */
    viewHeight: number;
}

/** What paper space holds: the frame, the inscription, the views without a scale and the viewports. */
export interface IDxfPaperSheet {
    entities: IDxfEntity[];
    viewports: IDxfViewport[];
}

/** The top-left corner of the screen sheet on paper: centred in the frame above the inscription. */
export function sheetOrigin(): IDxfPoint {
    const innerLeft: number = FRAME_MARGIN.left;
    const innerRight: number = DXF_PAPER_SIZE.width - FRAME_MARGIN.right;
    const innerTop: number = DXF_PAPER_SIZE.height - FRAME_MARGIN.top;
    const stampTop: number = FRAME_MARGIN.bottom + TITLE_BLOCK_SIZE.height;
    const width: number = SHEET_SIZE.width * PAPER_MM_PER_PX;
    const height: number = SHEET_SIZE.height * PAPER_MM_PER_PX;
    return [innerLeft + (innerRight - innerLeft - width) / 2, stampTop + (innerTop - stampTop + height) / 2];
}

function rectangle(layer: EDxfSheetLayer, x0: number, y0: number, x1: number, y1: number): IDxfLine[] {
    const corners: IDxfPoint[] = [
        [x0, y0],
        [x1, y0],
        [x1, y1],
        [x0, y1],
    ];
    return corners.map((from: IDxfPoint, index: number): IDxfLine => ({
        kind: EDxfEntityKind.Line,
        ltScale: 1,
        to: corners[(index + 1) % corners.length],
        layer,
        from,
    }));
}

/** The trimmed sheet edge (thin) and the frame (main line). */
function frameEntities(): IDxfLine[] {
    return [
        ...rectangle(EDxfSheetLayer.Thin, 0, 0, DXF_PAPER_SIZE.width, DXF_PAPER_SIZE.height),
        ...rectangle(
            EDxfSheetLayer.Frame,
            FRAME_MARGIN.left,
            FRAME_MARGIN.bottom,
            DXF_PAPER_SIZE.width - FRAME_MARGIN.right,
            DXF_PAPER_SIZE.height - FRAME_MARGIN.top
        ),
    ];
}

function viewportOf(view: IPlacedView, origin: IDxfPoint): IDxfViewport {
    const { x, y, w, h }: { x: number; y: number; w: number; h: number } = view.pane.rect;
    return {
        center: [origin[0] + (x + w / 2) * PAPER_MM_PER_PX, origin[1] - (y + h / 2) * PAPER_MM_PER_PX],
        width: w * PAPER_MM_PER_PX,
        height: h * PAPER_MM_PER_PX,
        viewCenter: [(view.window.minX + view.window.maxX) / 2, (view.window.minY + view.window.maxY) / 2],
        viewHeight: view.window.maxY - view.window.minY,
    };
}

/** A pane without a millimetre scale, drawn straight on the paper at its place on the sheet. */
function paperPane(pane: IPane, origin: IDxfPoint): IDxfEntity[] {
    const fit: IDxfPaneFit = { pxPerMm: 1 / PAPER_MM_PER_PX, origin: { x: 0, y: 0 }, paneId: pane.id, scale: 1 };
    const dx: number = origin[0] + pane.rect.x * PAPER_MM_PER_PX;
    const dy: number = origin[1] - pane.rect.y * PAPER_MM_PER_PX;
    return [...paneEntities(pane.projection.primitives, fit), titleEntity(pane, fit)].map((entity: IDxfEntity): IDxfEntity =>
        translateEntity(entity, dx, dy)
    );
}

/** Paper space: the frame, the inscription, a viewport per placed view, the unscaled panes. */
export function paperSheet(views: readonly IPlacedView[], panes: readonly IPane[], stamp: ITitleBlockData): IDxfPaperSheet {
    const origin: IDxfPoint = sheetOrigin();
    const placed: ReadonlySet<IPane> = new Set<IPane>(views.map((view: IPlacedView): IPane => view.pane));
    const stampAt: IDxfPoint = [DXF_PAPER_SIZE.width - FRAME_MARGIN.right - TITLE_BLOCK_SIZE.width, FRAME_MARGIN.bottom];
    return {
        entities: [
            ...frameEntities(),
            ...titleBlockEntities(stamp, stampAt),
            ...panes.filter((pane: IPane): boolean => !placed.has(pane)).flatMap((pane: IPane): IDxfEntity[] => paperPane(pane, origin)),
        ],
        viewports: views.map((view: IPlacedView): IDxfViewport => viewportOf(view, origin)),
    };
}
